/**
 * Tap the vessel and this sheet gives the detail on demand: position in degrees,
 * minutes and decimal minutes, SOG, COG and heading, and where the fix actually
 * comes from.
 *
 * It used to say "simulated" whatever the source, a phase-1 relic from before
 * the device GNSS seam existed, and a skipper sitting on a real fix rightly
 * asked what it was on about (fb-msmb0hd7-4). The pill now names the live
 * source, and on device GNSS the sheet shows the fix accuracy and age, the same
 * numbers the menu's GNSS panel reads, from the same stores, so the two can
 * never disagree.
 */
import { useChartPlotterStore } from '@/store';
import { usePositionSourceStore } from '@/store/positionSource';
import { useGnssStore } from '@/store/gnss';
import { formatFixAge } from '@/gnss/fixHealth';
import { formatLatLon, formatBearing } from '@/nav';
import { Sheet } from './Sheet';

export function VesselSheet() {
  const ownship = useChartPlotterStore((s) => s.ownship);
  const sourceKind = usePositionSourceStore((s) => s.sourceKind);
  const fixAgeSec = usePositionSourceStore((s) => s.fixAgeSec);
  const lastFix = useGnssStore((s) => s.lastFix);
  const moving = ownship.sog > 0;
  const device = sourceKind === 'device';

  const lat = ownship.position ? formatLatLon(ownship.position[1], 'lat') : '—';
  const lon = ownship.position ? formatLatLon(ownship.position[0], 'lon') : '—';
  const accM = lastFix?.horizontalErrorM;
  const accText = accM != null ? `±${accM < 10 ? accM.toFixed(1) : Math.round(accM)} m` : '—';

  return (
    <Sheet
      name="vessel"
      title="Our vessel"
      titleTag={<span className="acp-pill">{device ? 'device GNSS' : 'simulated'}</span>}
      sub={
        device
          ? 'Position from this device’s own GNSS receiver.'
          : 'Position from the simulated training boat. Switch to Device GNSS in Boat, Sensors & Downloads for a real fix.'
      }
    >
      <div className="acp-kv">
        <span className="k">Latitude</span>
        <span className="v">{lat}</span>
      </div>
      <div className="acp-kv">
        <span className="k">Longitude</span>
        <span className="v">{lon}</span>
      </div>
      <div className="acp-kv">
        <span className="k">SOG</span>
        <span className="v">{ownship.sog.toFixed(1)} kn</span>
      </div>
      <div className="acp-kv">
        <span className="k">COG</span>
        <span className="v">{moving ? formatBearing(ownship.cog) : 'Not moving yet'}</span>
      </div>
      <div className="acp-kv">
        <span className="k">Heading</span>
        {/* Position can be real while heading is not: there is no compass feed
            yet, so the heading is the helm's setting on either source. Saying
            so beats a number that quietly pretends to be a sensor. */}
        <span className="v">{formatBearing(ownship.heading)} ({device ? 'helm — no compass feed' : 'simulated'})</span>
      </div>
      {device && (
        <>
          <div className="acp-kv">
            <span className="k">Fix accuracy</span>
            <span className="v">{accText}</span>
          </div>
          <div className="acp-kv">
            <span className="k">Fix age</span>
            <span className="v">{fixAgeSec != null ? formatFixAge(fixAgeSec * 1000) : 'waiting for a fix'}</span>
          </div>
        </>
      )}
    </Sheet>
  );
}
